import { fetchAuthSession, signOut } from 'aws-amplify/auth';

export interface TokenInfo {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

export interface UserInfo {
  email: string;
  name: string;
  userId: string;
}

export class TokenService {
  private static instance: TokenService;
  private clientId: string;

  private constructor() {
    this.clientId = process.env.PUBLIC_USERPOOL_WEB_CLIENT_ID || '';
  }

  public static getInstance(): TokenService {
    if (!TokenService.instance) {
      TokenService.instance = new TokenService();
    }
    return TokenService.instance;
  }

  /**
   * Retrieves all tokens from localStorage and parses expiration
   */
  public getStoredTokens(): TokenInfo | null {
    try {
      const authUser = localStorage.getItem(
        `CognitoIdentityServiceProvider.${this.clientId}.LastAuthUser`,
      );

      if (!authUser) return null;

      const idToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${this.clientId}.${authUser}.idToken`,
      );
      const accessToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${this.clientId}.${authUser}.accessToken`,
      );
      const refreshToken = localStorage.getItem(
        `CognitoIdentityServiceProvider.${this.clientId}.${authUser}.refreshToken`,
      );

      if (!idToken || !accessToken || !refreshToken) return null;

      // Parse JWT to get expiration
      const expiresAt = this.getTokenExpiration(idToken);

      return {
        idToken,
        accessToken,
        refreshToken,
        expiresAt,
      };
    } catch (error) {
      console.error('Error retrieving stored tokens:', error);
      return null;
    }
  }

  /**
   * Checks if the current token is expired or will expire within buffer time
   */
  public isTokenExpired(bufferMinutes: number = 5): boolean {
    const tokens = this.getStoredTokens();
    if (!tokens) return true;

    const now = Date.now();
    const bufferMs = bufferMinutes * 60 * 1000;

    return now >= tokens.expiresAt - bufferMs;
  }

  /**
   * Refreshes the authentication session using AWS Amplify
   */
  public async refreshTokens(): Promise<TokenInfo | null> {
    try {
      // Use AWS Amplify's built-in token refresh
      const session = await fetchAuthSession({ forceRefresh: true });

      if (!session.tokens?.idToken?.toString()) {
        console.log('No ID token found after refresh', session);
        throw new Error('Failed to refresh tokens');
      }

      // After forceRefresh, Amplify has updated localStorage with new tokens
      // Get the updated tokens from localStorage instead of mixing session and stored tokens
      const refreshedTokens = this.getStoredTokens();

      if (!refreshedTokens) {
        throw new Error('Failed to retrieve refreshed tokens from storage');
      }

      return refreshedTokens;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, the user needs to re-authenticate
      await this.handleRefreshFailure();
      return null;
    }
  }

  /**
   * Gets the current valid ID token, refreshing if necessary
   */
  public async getCurrentToken(): Promise<string | null> {
    // Check if current token is valid
    if (!this.isTokenExpired()) {
      const tokens = this.getStoredTokens();
      return tokens?.idToken || null;
    }

    // Attempt to refresh
    const refreshedTokens = await this.refreshTokens();
    return refreshedTokens?.idToken || null;
  }

  /**
   * Gets the current valid access token, refreshing if necessary
   */
  public async getCurrentAccessToken(): Promise<string | null> {
    // Check if current token is valid
    if (!this.isTokenExpired()) {
      const tokens = this.getStoredTokens();
      return tokens?.accessToken || null;
    }

    // Attempt to refresh
    const refreshedTokens = await this.refreshTokens();
    return refreshedTokens?.accessToken || null;
  }

  /**
   * Handles refresh failure by signing out the user
   */
  private async handleRefreshFailure(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
      // Clear local storage manually if signOut fails
      this.clearTokens();
    }
  }

  /**
   * Clears all tokens from localStorage
   */
  private clearTokens(): void {
    const authUser = localStorage.getItem(
      `CognitoIdentityServiceProvider.${this.clientId}.LastAuthUser`,
    );

    if (authUser) {
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${this.clientId}.${authUser}.idToken`,
      );
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${this.clientId}.${authUser}.accessToken`,
      );
      localStorage.removeItem(
        `CognitoIdentityServiceProvider.${this.clientId}.${authUser}.refreshToken`,
      );
    }
  }

  /**
   * Extracts user information from the ID token
   */
  public getUserInfo(): UserInfo | null {
    try {
      const tokens = this.getStoredTokens();
      if (!tokens || !tokens.idToken) return null;

      const payload = JSON.parse(atob(tokens.idToken.split('.')[1]));

      return {
        email: payload.email || '',
        name: payload.name || payload.preferred_username || payload.email || '',
        userId: payload.sub || payload['cognito:username'] || '',
      };
    } catch (error) {
      console.error('Error extracting user info from token:', error);
      return null;
    }
  }

  /**
   * Parses JWT token to extract expiration timestamp
   */
  private getTokenExpiration(token: string): number {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Error parsing token expiration:', error);
      return 0;
    }
  }
}
